import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import './Settings.css';
import { Button } from '@/components/ui/button';

export default function Settings() {
  // Whitelist state
  const [newDomain, setNewDomain] = useState('');
  const [newReason, setNewReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Visibility scorer state
  const [osnDomains, setOsnDomains] = useState<string[]>([]);
  const [alpha, setAlpha] = useState<number>(1.5);
  const [beta, setBeta] = useState<number>(1.0);
  const [newOsnDomain, setNewOsnDomain] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Known pirate domain patterns (regex)
  const [piratePatterns, setPiratePatterns] = useState<string[]>([]);
  const [newPiratePattern, setNewPiratePattern] = useState('');
  // Known pirate domains (simple)
  const [pirateDomains, setPirateDomains] = useState<string[]>([]);
  const [newPirateDomain, setNewPirateDomain] = useState('');
  // Trusted news/media domains
  const [trustedDomains, setTrustedDomains] = useState<string[]>([]);
  const [newTrustedDomain, setNewTrustedDomain] = useState('');

  // Classifier tunables
  const DEFAULTS = {
    violationThreshold: 65,
    llmConfidenceThreshold: 85,
    ugcMinDurationMinutes: 20,
    episodeListMinCount: 3,
  } as const;

  // Visibility defaults (used for Reset)
  const VIS_DEFAULTS = {
    alpha: 1.5,
    beta: 1.0,
    osnDomains: [
      'osnplus.com',
      'osn.com',
      'watch.osn.com',
      'www.osnplus.com',
      'www.osn.com',
    ],
  } as const;

  const [violationThreshold, setViolationThreshold] = useState<number>(DEFAULTS.violationThreshold);
  const [llmConfidenceThreshold, setLlmConfidenceThreshold] = useState<number>(DEFAULTS.llmConfidenceThreshold);
  const [ugcMinDurationMinutes, setUgcMinDurationMinutes] = useState<number>(DEFAULTS.ugcMinDurationMinutes);
  const [episodeListMinCount, setEpisodeListMinCount] = useState<number>(DEFAULTS.episodeListMinCount);

  // Secondary confirm pass
  const [secondaryConfirmEnabled, setSecondaryConfirmEnabled] = useState<boolean>(false);
  const [secondaryMinConfidence, setSecondaryMinConfidence] = useState<number>(80);
  const [secondaryModel, setSecondaryModel] = useState<string>('x-ai/grok-4-fast');

  // Reachability throttle settings
  const REACHABILITY_DEFAULTS = {
    delayMs: 500,
    cooldownHours: 24,
    maxPerHour: 100,
  } as const;

  const [reachabilityDelayMs, setReachabilityDelayMs] = useState<number>(REACHABILITY_DEFAULTS.delayMs);
  const [reachabilityCooldownHours, setReachabilityCooldownHours] = useState<number>(REACHABILITY_DEFAULTS.cooldownHours);
  const [reachabilityMaxPerHour, setReachabilityMaxPerHour] = useState<number>(REACHABILITY_DEFAULTS.maxPerHour);

  // Queries and mutations
  const whitelist = useQuery(api.whitelist.list);
  const config = useQuery(api.settings.getVisibilityScorerConfig);
  const piratePatternsSetting = useQuery(api.settings.getSetting, { key: 'pirateDomainPatterns' });
  const pirateDomainsSetting = useQuery(api.settings.getSetting, { key: 'pirateDomains' });
  const trustedDomainsSetting = useQuery(api.settings.getSetting, { key: 'trustedNewsDomains' });
  const vtSetting: any = useQuery(api.settings.getSetting, { key: 'violationThreshold' });
  const llmConfSetting: any = useQuery(api.settings.getSetting, { key: 'llmConfidenceThreshold' });
  const ugcMinSetting: any = useQuery(api.settings.getSetting, { key: 'ugcMinDurationMinutes' });
  const epMinSetting: any = useQuery(api.settings.getSetting, { key: 'episodeListMinCount' });
  const secondaryConfirmSetting: any = useQuery(api.settings.getSetting, { key: 'secondaryConfirmEnabled' });
  const secondaryMinConfSetting: any = useQuery(api.settings.getSetting, { key: 'secondaryMinConfidence' });
  const secondaryModelSetting: any = useQuery(api.settings.getSetting, { key: 'secondaryModel' });
  const reachDelaySettings: any = useQuery(api.settings.getSetting, { key: 'reachabilityDelayMs' });
  const reachCooldownSetting: any = useQuery(api.settings.getSetting, { key: 'reachabilityCooldownHours' });
  const reachMaxPerHourSetting: any = useQuery(api.settings.getSetting, { key: 'reachabilityMaxPerHour' });
  const addDomain = useMutation(api.whitelist.add);
  const removeDomain = useMutation(api.whitelist.remove);
  const initializeDefaults = useMutation(api.whitelist.initializeDefaults);
  const updateConfig = useMutation(api.settings.updateVisibilityScorerConfig);
  const updateSetting = useMutation(api.settings.updateSetting);

  // Load visibility config when available
  useEffect(() => {
    if (config) {
      setOsnDomains(config.osnDomains);
      setAlpha(config.alpha);
      setBeta(config.beta);
    }
  }, [config]);

  useEffect(() => {
    if (Array.isArray(piratePatternsSetting)) {
      setPiratePatterns(piratePatternsSetting as string[]);
    } else if (piratePatternsSetting === null) {
      setPiratePatterns([]);
    }
  }, [piratePatternsSetting]);

  useEffect(() => {
    if (Array.isArray(pirateDomainsSetting)) {
      setPirateDomains(pirateDomainsSetting as string[]);
    } else if (pirateDomainsSetting === null) {
      setPirateDomains([]);
    }
  }, [pirateDomainsSetting]);

  useEffect(() => {
    if (Array.isArray(trustedDomainsSetting)) {
      setTrustedDomains(trustedDomainsSetting as string[]);
    } else if (trustedDomainsSetting === null) {
      setTrustedDomains([]);
    }
  }, [trustedDomainsSetting]);

  useEffect(() => {
    if (typeof vtSetting === 'number') setViolationThreshold(vtSetting);
    if (typeof llmConfSetting === 'number') setLlmConfidenceThreshold(llmConfSetting);
    if (typeof ugcMinSetting === 'number') setUgcMinDurationMinutes(ugcMinSetting);
    if (typeof epMinSetting === 'number') setEpisodeListMinCount(epMinSetting);
  }, [vtSetting, llmConfSetting, ugcMinSetting, epMinSetting]);

  useEffect(() => {
    if (typeof secondaryConfirmSetting === 'boolean') setSecondaryConfirmEnabled(secondaryConfirmSetting);
    if (typeof secondaryMinConfSetting === 'number') setSecondaryMinConfidence(secondaryMinConfSetting);
    if (typeof secondaryModelSetting === 'string') setSecondaryModel(secondaryModelSetting);
  }, [secondaryConfirmSetting, secondaryMinConfSetting, secondaryModelSetting]);

  useEffect(() => {
    if (typeof reachDelaySettings === 'number') setReachabilityDelayMs(reachDelaySettings);
    if (typeof reachCooldownSetting === 'number') setReachabilityCooldownHours(reachCooldownSetting);
    if (typeof reachMaxPerHourSetting === 'number') setReachabilityMaxPerHour(reachMaxPerHourSetting);
  }, [reachDelaySettings, reachCooldownSetting, reachMaxPerHourSetting]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newDomain.trim()) {
      setError('Domain is required');
      return;
    }

    try {
      await addDomain({
        domain: newDomain.trim(),
        reason: newReason.trim() || undefined,
      });
      setSuccess(`Added ${newDomain.trim()} to whitelist`);
      setNewDomain('');
      setNewReason('');
    } catch (err: any) {
      setError(err.message || 'Failed to add domain');
    }
  };

  const handleRemove = async (id: Id<'whitelist'>) => {
    try {
      await removeDomain({ id });
      setSuccess('Domain removed from whitelist');
    } catch (err: any) {
      setError(err.message || 'Failed to remove domain');
    }
  };

  const handleInitialize = async () => {
    try {
      const result = await initializeDefaults();
      if (result.initialized) {
        setSuccess(`Initialized ${result.count} default domains`);
      } else {
        setError('Whitelist already has entries');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize defaults');
    }
  };

  // Visibility scorer handlers
  const handleAddOsnDomain = () => {
    const domain = newOsnDomain.trim().toLowerCase();
    if (domain && !osnDomains.includes(domain)) {
      setOsnDomains([...osnDomains, domain]);
      setNewOsnDomain('');
    }
  };

  const handleRemoveOsnDomain = (domain: string) => {
    setOsnDomains(osnDomains.filter(d => d !== domain));
  };

  const handleSaveVisibilityConfig = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateConfig({
        osnDomains,
        alpha,
        beta,
      });

      setSuccess('✅ Visibility configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save visibility configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetVisibilityConfig = () => {
    setOsnDomains([...VIS_DEFAULTS.osnDomains]);
    setAlpha(VIS_DEFAULTS.alpha);
    setBeta(VIS_DEFAULTS.beta);
    setSuccess('↺ Restored visibility defaults — click Save to persist');
    setTimeout(() => setSuccess(''), 2500);
  };

  // Pirate patterns
  const handleAddPiratePattern = () => {
    const p = newPiratePattern.trim();
    if (!p) return;
    if (!piratePatterns.includes(p)) {
      setPiratePatterns([...piratePatterns, p]);
      setNewPiratePattern('');
    }
  };
  const handleRemovePiratePattern = (pattern: string) => {
    setPiratePatterns(piratePatterns.filter(p => p !== pattern));
  };
  const handleSavePiratePatterns = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateSetting({ key: 'pirateDomainPatterns', value: piratePatterns });
      setSuccess('✅ Pirate regex patterns saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save pirate regex patterns');
    } finally {
      setIsSaving(false);
    }
  };

  // Pirate domains
  const handleAddPirateDomain = () => {
    const d = newPirateDomain.trim().toLowerCase();
    if (!d) return;
    if (!pirateDomains.includes(d)) {
      setPirateDomains([...pirateDomains, d]);
      setNewPirateDomain('');
    }
  };
  const handleRemovePirateDomain = (domain: string) => {
    setPirateDomains(pirateDomains.filter(x => x !== domain));
  };
  const handleSavePirateDomains = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateSetting({ key: 'pirateDomains', value: pirateDomains });
      setSuccess('✅ Pirate domains saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save pirate domains');
    } finally {
      setIsSaving(false);
    }
  };

  // Trusted news/media domains
  const handleAddTrustedDomain = () => {
    const d = newTrustedDomain.trim().toLowerCase();
    if (!d) return;
    if (!trustedDomains.includes(d)) {
      setTrustedDomains([...trustedDomains, d]);
      setNewTrustedDomain('');
    }
  };
  const handleRemoveTrustedDomain = (domain: string) => {
    setTrustedDomains(trustedDomains.filter(x => x !== domain));
  };
  const handleSaveTrustedDomains = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateSetting({ key: 'trustedNewsDomains', value: trustedDomains });
      setSuccess('✅ Trusted news/media domains saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save trusted domains');
    } finally {
      setIsSaving(false);
    }
  };

  // Classifier tunables save
  const handleSaveTunables = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all([
        updateSetting({ key: 'violationThreshold', value: violationThreshold }),
        updateSetting({ key: 'llmConfidenceThreshold', value: llmConfidenceThreshold }),
        updateSetting({ key: 'ugcMinDurationMinutes', value: ugcMinDurationMinutes }),
        updateSetting({ key: 'episodeListMinCount', value: episodeListMinCount }),
        updateSetting({ key: 'secondaryConfirmEnabled', value: secondaryConfirmEnabled }),
        updateSetting({ key: 'secondaryMinConfidence', value: secondaryMinConfidence }),
        updateSetting({ key: 'secondaryModel', value: secondaryModel }),
      ]);
      setSuccess('✅ Classifier settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save classifier settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetTunables = async () => {
    setViolationThreshold(DEFAULTS.violationThreshold);
    setLlmConfidenceThreshold(DEFAULTS.llmConfidenceThreshold);
    setUgcMinDurationMinutes(DEFAULTS.ugcMinDurationMinutes);
    setEpisodeListMinCount(DEFAULTS.episodeListMinCount);
    setSecondaryConfirmEnabled(false);
    setSecondaryMinConfidence(80);
    setSecondaryModel('x-ai/grok-4-fast');
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all([
        updateSetting({ key: 'violationThreshold', value: DEFAULTS.violationThreshold }),
        updateSetting({ key: 'llmConfidenceThreshold', value: DEFAULTS.llmConfidenceThreshold }),
        updateSetting({ key: 'ugcMinDurationMinutes', value: DEFAULTS.ugcMinDurationMinutes }),
        updateSetting({ key: 'episodeListMinCount', value: DEFAULTS.episodeListMinCount }),
        updateSetting({ key: 'secondaryConfirmEnabled', value: false }),
        updateSetting({ key: 'secondaryMinConfidence', value: 80 }),
        updateSetting({ key: 'secondaryModel', value: 'x-ai/grok-2-latest' }),
      ]);
      setSuccess('↺ Classifier settings reset to defaults');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err.message || 'Failed to reset classifier settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Reachability throttle handlers
  const handleSaveReachabilitySettings = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all([
        updateSetting({ key: 'reachabilityDelayMs', value: reachabilityDelayMs }),
        updateSetting({ key: 'reachabilityCooldownHours', value: reachabilityCooldownHours }),
        updateSetting({ key: 'reachabilityMaxPerHour', value: reachabilityMaxPerHour }),
      ]);
      setSuccess('✅ Proxy reachability settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save reachability settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetReachabilitySettings = async () => {
    setReachabilityDelayMs(REACHABILITY_DEFAULTS.delayMs);
    setReachabilityCooldownHours(REACHABILITY_DEFAULTS.cooldownHours);
    setReachabilityMaxPerHour(REACHABILITY_DEFAULTS.maxPerHour);
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all([
        updateSetting({ key: 'reachabilityDelayMs', value: REACHABILITY_DEFAULTS.delayMs }),
        updateSetting({ key: 'reachabilityCooldownHours', value: REACHABILITY_DEFAULTS.cooldownHours }),
        updateSetting({ key: 'reachabilityMaxPerHour', value: REACHABILITY_DEFAULTS.maxPerHour }),
      ]);
      setSuccess('↺ Reachability settings reset to defaults');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err.message || 'Failed to reset reachability settings');
    } finally {
      setIsSaving(false);
    }
  };

  // (deduped handlers defined above)

  return (
    <div className="settings">

      {/* Known Pirate Domains (simple) */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>Known Pirate Domains</h3>
          <p className="settings-description">
            Enter simple domains or labels. Examples: <code>egybest.com</code> (exact TLD), <code>egybest</code> (any TLD). Subdomains match automatically.
          </p>
        </div>

        <div className="osn-domain-list">
          {pirateDomains.length === 0 && (
            <div className="empty-list">No pirate domains configured yet.</div>
          )}
          {pirateDomains.map((domain, index) => (
            <div key={`${domain}-${index}`} className="osn-domain-item">
              <span className="osn-domain-name">{domain}</span>
              <button
                className="btn-remove-osn"
                onClick={() => handleRemovePirateDomain(domain)}
                title="Remove domain"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="add-osn-domain-form">
          <input
            type="text"
            placeholder="e.g., egybest.com or egybest"
            value={newPirateDomain}
            onChange={(e) => setNewPirateDomain(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddPirateDomain()}
            className="osn-domain-input"
          />
          <Button onClick={handleAddPirateDomain}>
            + Add Domain
          </Button>
        </div>

        <div className="visibility-actions">
          <Button
            variant="outline"
            className="inline-flex items-center border px-4 py-2 rounded-md"
            onClick={handleSavePirateDomains}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : '💾 Save Pirate Domains'}
          </Button>
        </div>
      </div>

      {/* Trusted News/Media Domains */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>Trusted News/Media Domains</h3>
          <p className="settings-description">
            Add domains for legitimate news/media sites that may mention content but are NOT piracy sources
            (e.g., <code>primetimer.com</code>, <code>variety.com</code>, <code>bbc.com</code>). These domains
            get a <strong>90% score reduction</strong> (can still be flagged if score is 10× higher than threshold).
            For complete exclusion, use the Whitelist instead.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="osn-domain-list">
          {trustedDomains.length === 0 && (
            <div className="empty-list">No trusted domains configured yet. Click Save to load defaults (25 domains).</div>
          )}
          {trustedDomains.map((domain, index) => (
            <div key={`${domain}-${index}`} className="osn-domain-item">
              <span className="osn-domain-name">{domain}</span>
              <button
                className="btn-remove-osn"
                onClick={() => handleRemoveTrustedDomain(domain)}
                title="Remove domain"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="add-osn-domain-form">
          <input
            type="text"
            placeholder="e.g., nytimes.com or theguardian.com"
            value={newTrustedDomain}
            onChange={(e) => setNewTrustedDomain(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTrustedDomain()}
            className="osn-domain-input"
          />
          <Button onClick={handleAddTrustedDomain}>
            + Add Trusted Domain
          </Button>
        </div>

        <div className="visibility-actions">
          <Button
            variant="outline"
            className="inline-flex items-center border px-4 py-2 rounded-md"
            onClick={handleSaveTrustedDomains}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : '💾 Save Trusted Domains'}
          </Button>
        </div>
      </div>

      {/* Known Pirate Domain Patterns (Regex) */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>Known Pirate Domain Patterns (Regex)</h3>
          <p className="settings-description">
            Add regular expressions that match known pirate domains (e.g., <code>(^|\\.)egybest\\.</code>). Patterns are case-insensitive. This is optional if you maintain the simple domains list above — we automatically translate domains to regex.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="osn-domain-list">
          {piratePatterns.length === 0 && (
            <div className="empty-list">No patterns configured yet.</div>
          )}
          {piratePatterns.map((pattern, index) => (
            <div key={`${pattern}-${index}`} className="osn-domain-item">
              <span className="osn-domain-name">{pattern}</span>
              <button
                className="btn-remove-osn"
                onClick={() => handleRemovePiratePattern(pattern)}
                title="Remove pattern"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="add-osn-domain-form">
          <input
            type="text"
            placeholder="Enter regex (e.g., (^|\\.)egybest\\.)"
            value={newPiratePattern}
            onChange={(e) => setNewPiratePattern(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddPiratePattern()}
            className="osn-domain-input"
          />
          <Button onClick={handleAddPiratePattern}>+ Add Pattern</Button>
        </div>

        <div className="visibility-actions">
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleSavePiratePatterns} disabled={isSaving}>
            {isSaving ? 'Saving...' : '💾 Save Pirate Patterns'}
          </Button>
        </div>
      </div>

      {/* Classifier Tunables */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>Classifier Tunables</h3>
          <p className="settings-description">
            Adjust thresholds to trade off recall vs precision. Lowering values increases detections; raising values reduces false positives.
          </p>
        </div>

        <div className="tunables-grid">
          <label className="tunables-item">
            <span>Violation Threshold <span className="help-icon" title="Minimum heuristic score (0–100) to flag a page as a violation. Lower = more detections (more false positives); Higher = fewer detections (fewer false positives)">ℹ️</span></span>
            <input type="number" min={0} max={100} value={violationThreshold}
                   onChange={(e) => setViolationThreshold(Number(e.target.value))} />
            <small>Minimum heuristic score (0–100) to create a violation.</small>
          </label>
          <label className="tunables-item">
            <span>LLM Confidence Threshold <span className="help-icon" title="Confidence (0–100) required to trust the LLM’s answer directly. Below this, we fall back to heuristics.">ℹ️</span></span>
            <input type="number" min={0} max={100} value={llmConfidenceThreshold}
                   onChange={(e) => setLlmConfidenceThreshold(Number(e.target.value))} />
            <small>Recommended: 85–90. Below this, results are double-checked by heuristics.</small>
          </label>
          <label className="tunables-item">
            <span>UGC Min Duration (minutes) <span className="help-icon" title="For social/UGC hosts, require videos to be at least this long to count as likely full episode/film">ℹ️</span></span>
            <input type="number" min={1} max={240} value={ugcMinDurationMinutes}
                   onChange={(e) => setUgcMinDurationMinutes(Number(e.target.value))} />
            <small>Duration gate for UGC hosts (e.g., ≥20 minutes).</small>
          </label>
          <label className="tunables-item">
            <span>Episode List Min Count <span className="help-icon" title="Minimum number of episode/season indicators to treat a page as a series index (boosts confidence)">ℹ️</span></span>
            <input type="number" min={1} max={100} value={episodeListMinCount}
                   onChange={(e) => setEpisodeListMinCount(Number(e.target.value))} />
            <small>Minimum matches to flag episode index pages.</small>
          </label>
        </div>

        <div className="visibility-actions">
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleSaveTunables} disabled={isSaving}>
            {isSaving ? 'Saving...' : '💾 Save Classifier Settings'}
          </Button>
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleResetTunables} disabled={isSaving} title="Reset thresholds to recommended defaults">
            ↺ Reset to Defaults
          </Button>
        </div>
      </div>

      {/* AI Validation (Secondary Confirm) */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>AI Validation — Secondary Confirm</h3>
          <p className="settings-description">Borderline or social media pages can be double-checked by a fast model that must agree to keep a positive classification.</p>
        </div>
        <div className="tunables-grid">
          <label className="tunables-item">
            <span>Enable Secondary Confirm</span>
            <input type="checkbox" checked={secondaryConfirmEnabled} onChange={(e) => setSecondaryConfirmEnabled(e.target.checked)} />
            <small>Applies to social URLs and low‑confidence primary results.</small>
          </label>
          <label className="tunables-item">
            <span>Secondary Min Confidence</span>
            <input type="number" min={0} max={100} value={secondaryMinConfidence} onChange={(e) => setSecondaryMinConfidence(Number(e.target.value))} />
            <small>Confidence required from confirm model (default 80).</small>
          </label>
          <label className="tunables-item">
            <span>Secondary Model (OpenRouter)</span>
            <input type="text" value={secondaryModel} onChange={(e) => setSecondaryModel(e.target.value)} />
            <small>Example: x-ai/grok-2-latest (fast).</small>
          </label>
        </div>
        <div className="visibility-actions">
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleSaveTunables} disabled={isSaving}>
            {isSaving ? 'Saving...' : '💾 Save AI Validation Settings'}
          </Button>
        </div>
      </div>

      {/* Proxy Reachability Throttle Settings */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>🌍 Proxy Reachability Throttle</h3>
          <p className="settings-description">
            Control how often regional reachability checks (MENA proxies) are performed to prevent overwhelming the proxy infrastructure.
          </p>
        </div>

        <div className="tunables-grid">
          <label className="tunables-item">
            <span>Delay Between Checks (ms) <span className="help-icon" title="Milliseconds to wait between checking different domains. Higher values = slower but safer.">ℹ️</span></span>
            <input type="number" min={100} max={5000} step={100} value={reachabilityDelayMs}
                   onChange={(e) => setReachabilityDelayMs(Number(e.target.value))} />
            <small>Wait time between domain checks (default: 500ms)</small>
          </label>
          <label className="tunables-item">
            <span>Cooldown Period (hours) <span className="help-icon" title="Hours to wait before re-checking the same domain. Prevents repeated checks of the same site.">ℹ️</span></span>
            <input type="number" min={1} max={168} value={reachabilityCooldownHours}
                   onChange={(e) => setReachabilityCooldownHours(Number(e.target.value))} />
            <small>Minimum hours between checks for same domain (default: 24h)</small>
          </label>
          <label className="tunables-item">
            <span>Max Checks Per Hour <span className="help-icon" title="Maximum number of times a single domain can be checked within a 1-hour window.">ℹ️</span></span>
            <input type="number" min={1} max={500} value={reachabilityMaxPerHour}
                   onChange={(e) => setReachabilityMaxPerHour(Number(e.target.value))} />
            <small>Rate limit per domain per hour (default: 100)</small>
          </label>
        </div>

        <div className="visibility-actions">
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleSaveReachabilitySettings} disabled={isSaving}>
            {isSaving ? 'Saving...' : '💾 Save Reachability Settings'}
          </Button>
          <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleResetReachabilitySettings} disabled={isSaving} title="Reset to recommended defaults">
            ↺ Reset to Defaults
          </Button>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-header">
          <h3>Whitelisted Domains</h3>
          <p className="settings-description">
            Sites in this list will be ignored during scans to avoid wasting API credits on legitimate services.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form className="whitelist-form" onSubmit={handleAdd}>
          <div className="form-row">
            <input
              type="text"
              placeholder="Domain (e.g., netflix.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              className="domain-input"
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              className="reason-input"
            />
            <Button type="submit">Add Domain</Button>
          </div>
        </form>

        {!whitelist || whitelist.length === 0 ? (
          <div className="empty-state">
            <p>No whitelisted domains yet.</p>
            <Button variant="secondary" onClick={handleInitialize}>
              Initialize Default Domains
            </Button>
          </div>
        ) : (
          <div className="whitelist-table">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Reason</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map((entry) => (
                  <tr key={entry._id}>
                    <td className="domain-cell">{entry.domain}</td>
                    <td>{entry.reason || '-'}</td>
                    <td>{new Date(entry.addedAt).toLocaleDateString()}</td>
                    <td>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemove(entry._id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visibility Scorer Configuration */}
      <div className="settings-section">
        <div className="settings-header">
          <h3>🔍 Search Visibility Configuration</h3>
          <p className="settings-description">
            Configure the Search Visibility Quality Score (SQS) parameters and OSN+ domains.
          </p>
        </div>

        <div className="visibility-subsection">
          <h4>OSN+ Official Domains</h4>
          <p className="subsection-description">
            Define which domains are considered official OSN+ properties for visibility tracking.
          </p>

          <div className="osn-domain-list">
            {osnDomains.map((domain, index) => (
              <div key={index} className="osn-domain-item">
                <span className="osn-domain-name">{domain}</span>
                <button
                  className="btn-remove-osn"
                  onClick={() => handleRemoveOsnDomain(domain)}
                  title="Remove domain"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="add-osn-domain-form">
            <input
              type="text"
              placeholder="Enter OSN+ domain (e.g., osnplus.com)"
              value={newOsnDomain}
              onChange={(e) => setNewOsnDomain(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddOsnDomain()}
              className="osn-domain-input"
            />
            <Button onClick={handleAddOsnDomain} className="inline-flex items-center px-4 py-2">+ Add OSN+ Domain</Button>
          </div>
        </div>

        <div className="visibility-subsection">
          <h4>Visibility Score Parameters</h4>
          <p className="subsection-description">
            Formula: SQS = 100 × (1 - (R-1)/N)<sup>α</sup> × (1 - P/R)<sup>β</sup>
          </p>

          <div className="parameter-controls">
            <div className="parameter-control">
              <label htmlFor="alpha">
                <strong>Alpha (α) - Ranking Sensitivity</strong>
                <span className="param-hint">Higher = more penalty for lower ranks</span>
              </label>
              <div className="slider-group">
                <input
                  id="alpha"
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={alpha}
                  onChange={(e) => setAlpha(parseFloat(e.target.value))}
                  className="param-slider"
                />
                <input
                  type="number"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={alpha}
                  onChange={(e) => setAlpha(parseFloat(e.target.value))}
                  className="param-number"
                />
              </div>
              <div className="param-info">Current: {alpha.toFixed(1)} (Default: 1.5)</div>
            </div>

            <div className="parameter-control">
              <label htmlFor="beta">
                <strong>Beta (β) - Pirate Interference Sensitivity</strong>
                <span className="param-hint">Higher = more penalty for pirates before OSN+</span>
              </label>
              <div className="slider-group">
                <input
                  id="beta"
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={beta}
                  onChange={(e) => setBeta(parseFloat(e.target.value))}
                  className="param-slider"
                />
                <input
                  type="number"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={beta}
                  onChange={(e) => setBeta(parseFloat(e.target.value))}
                  className="param-number"
                />
              </div>
              <div className="param-info">Current: {beta.toFixed(1)} (Default: 1.0)</div>
            </div>
          </div>

          <div className="visibility-actions">
            <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleResetVisibilityConfig} disabled={isSaving}>
              Reset
            </Button>
            <Button variant="outline" className="inline-flex items-center border px-4 py-2 rounded-md" onClick={handleSaveVisibilityConfig} disabled={isSaving}>
              {isSaving ? 'Saving...' : '💾 Save Visibility Config'}
            </Button>
          </div>
        </div>

        <div className="visibility-info-box">
          <h4>ℹ️ About Search Visibility Scoring</h4>
          <p>
            The Search Visibility Quality Score (SQS) measures how well OSN+ appears in search results
            relative to pirate sites. A score of 100 means perfect visibility (OSN+ ranked #1 with no pirates).
          </p>
          <ul>
            <li><strong>90-100:</strong> Excellent visibility, minimal pirate presence</li>
            <li><strong>70-89:</strong> Good visibility, some pirate competition</li>
            <li><strong>50-69:</strong> Moderate, significant pirate interference</li>
            <li><strong>30-49:</strong> Poor, heavy pirate presence</li>
            <li><strong>0-29:</strong> Critical, OSN+ severely outranked</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
